import React from 'react';
import { Card } from '../../ui/card';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Music2, Calendar, Mail } from 'lucide-react';

const COLORS = ['#f97316', '#22c55e', '#eab308', '#06b6d4', '#ec4899', '#8b5cf6'];

const TOOLTIP_STYLES = {
  orange: { backgroundColor: '#000', border: '1px solid #f97316' },
  green: { backgroundColor: '#000', border: '1px solid #22c55e' },
  purple: { backgroundColor: '#000', border: '1px solid #a855f7' },
};
const LINE_DOT_STYLE = { fill: '#f97316' };

const PieChartCard = ({ title, data, icon: Icon, borderColor, tooltipBorder, onClick }) => {
  if (!data.length) return null;
  return (
    <Card onClick={onClick}
      className={`bg-black/50 ${borderColor} p-6 cursor-pointer hover:shadow-lg transition-all duration-300`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <Icon className="w-5 h-5 mr-2" style={{ color: tooltipBorder }} /> {title}
        </h3>
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" labelLine={false}
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            outerRadius={80} fill="#8884d8" dataKey="value">
            {data.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ backgroundColor: '#000', border: `1px solid ${tooltipBorder}` }} />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  );
};

export const DashboardCharts = ({ analytics, navigate }) => {
  const releaseTypeData = Object.entries(analytics.releases_by_type || {}).map(([name, value]) => ({ name, value }));
  const releaseGenreData = Object.entries(analytics.releases_by_genre || {}).map(([name, value]) => ({ name, value }));
  const contactsStatusData = Object.entries(analytics.contacts_by_status || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1), value,
  }));
  const eventsByMonthData = Object.entries(analytics.events_by_month || {}).sort().map(([month, count]) => ({ month, events: count }));
  const contactsByMonthData = Object.entries(analytics.contacts_by_month || {}).sort().map(([month, count]) => ({ month, contacts: count }));

  return (
    <>
      {/* Row 1: Pie charts + bar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <PieChartCard title="Releases by Type" data={releaseTypeData} icon={TrendingUp}
          borderColor="border-orange-500/30 hover:border-orange-500 hover:shadow-orange-500/30"
          tooltipBorder="#f97316" onClick={() => navigate('/admin/releases')} />
        <PieChartCard title="Releases by Genre" data={releaseGenreData} icon={Music2}
          borderColor="border-green-500/30 hover:border-green-500 hover:shadow-green-500/30"
          tooltipBorder="#22c55e" onClick={() => navigate('/admin/releases')} />
        {contactsStatusData.length > 0 && (
          <Card onClick={() => navigate('/admin/contacts')}
            className="bg-black/50 border-purple-500/30 p-6 cursor-pointer hover:border-purple-500 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center">
                <Mail className="w-5 h-5 mr-2 text-purple-400" /> Messages by Status
              </h3>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={contactsStatusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="name" stroke="#888" /><YAxis stroke="#888" />
                <Tooltip contentStyle={TOOLTIP_STYLES.purple} />
                <Bar dataKey="value" fill="#a855f7" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Row 2: Timeline charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {eventsByMonthData.length > 0 && (
          <Card onClick={() => navigate('/admin/events')}
            className="bg-black/50 border-green-500/30 p-6 cursor-pointer hover:border-green-500 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-green-400" /> Events by Month
              </h3>
              <span className="text-green-400 text-sm">Click to manage &rarr;</span>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={eventsByMonthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#888" /><YAxis stroke="#888" />
                <Tooltip contentStyle={TOOLTIP_STYLES.green} />
                <Bar dataKey="events" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
        {contactsByMonthData.length > 0 && (
          <Card onClick={() => navigate('/admin/contacts')}
            className="bg-black/50 border-orange-500/30 p-6 cursor-pointer hover:border-orange-500 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center">
                <Mail className="w-5 h-5 mr-2 text-orange-400" /> Contact Messages Trend
              </h3>
              <span className="text-orange-400 text-sm">Click to view &rarr;</span>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={contactsByMonthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#888" /><YAxis stroke="#888" />
                <Tooltip contentStyle={TOOLTIP_STYLES.orange} />
                <Legend />
                <Line type="monotone" dataKey="contacts" stroke="#f97316" strokeWidth={2} dot={LINE_DOT_STYLE} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </>
  );
};
